import { ExpandableRowDef } from '@/components';
import { ArrowCircleDown } from '@/components/Icons';
import { ColumnDef } from '@/components/Table/types';
import { twJoin, twMerge } from 'tailwind-merge';
import { alignStyles } from './styles';

interface ExpandableTableRowCellProps<R> {
  row: ExpandableRowDef<R>;
  columns: Array<ColumnDef<R>>;
  depth?: number;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  className?: string;
}

export const ExpandableTableRowCell = <R,>({
  row,
  columns,
  depth,
  isOpen,
  setIsOpen,
  className,
}: ExpandableTableRowCellProps<R>) => {
  const firstColumn = columns[0];

  const tableDataStyling = twMerge(
    'p-2.5 border-gray-200 border-t border-b text-gray text-sm',
    depth && 'bg-gray-100 text-gray-400',
    className,
  );

  return (
    <tr
      onClick={() => setIsOpen?.(!isOpen)}
      className={twMerge(row.rows?.length && 'cursor-pointer')}
    >
      <td
        key={`${firstColumn.id}-item-${row.id}`}
        className={twMerge(tableDataStyling, firstColumn.tdClassNames)}
        align={firstColumn.alignment ?? 'center'}
      >
        <div
          className={twMerge(
            'flex items-center gap-3',
            firstColumn.rowContainerClassNames,
          )}
          style={{ paddingLeft: `${(depth ?? 0) * 32}px` }}
        >
          <ArrowCircleDown
            className={twJoin(
              'size-5 text-blue-500 transition-transform',
              !isOpen && 'rotate-180',
              !row.rows?.length && 'hidden',
            )}
          />
          <div
            key={`${firstColumn.id}-row-${row.id}`}
            tabIndex={0}
            className={twJoin(
              'inline-flex w-[inherit] whitespace-nowrap',
              firstColumn.alignment
                ? alignStyles[firstColumn.alignment]
                : 'justify-center',
            )}
          >
            {firstColumn.renderRow
              ? firstColumn.renderRow(row)
              : `${row[firstColumn.accessor]}`}
          </div>
        </div>
      </td>
      {columns.slice(1).map((column) => {
        return (
          <td
            key={`${column.id}-item-${row.id}`}
            className={twMerge(tableDataStyling, column.tdClassNames)}
            align={column.alignment ?? 'center'}
          >
            <div
              key={`${column.id}-row-${row.id}`}
              tabIndex={0}
              className={twJoin(
                'inline-flex w-[inherit] whitespace-nowrap',
                column.rowContainerClassNames,
                column.alignment
                  ? alignStyles[column.alignment]
                  : 'justify-center',
              )}
            >
              {column.renderRow
                ? column.renderRow(row)
                : `${row[column.accessor]}`}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
